import { useEffect, useState } from "react"
import { Device } from "../../types/api"
import { useNavigate } from "react-router-dom"
import { deleteDevice, listDevices } from "../../api/device"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { Box, Button, Paper, TextField, Typography } from "@mui/material"
import { Add, Delete, Edit, Search } from "@mui/icons-material"
import Swal from "sweetalert2"

export default function DeviceListPage() {
    const [device, setDevices] = useState<Device[]>([])
    const [total, setTotal] = useState(0)
    const [pageSize, setPageSize] = useState(10)
    const [page, setPage] = useState(0)
    const [searchBoxId, setSearchBoxId] = useState('')
    const navigate = useNavigate()

    // 👉 role จาก localStorage
    const role = (localStorage.getItem("role") || "").toLowerCase()
    const isInspector = role === "inspector"

    const load = async () => {
        try {
            const { list, total } = await listDevices({
                take: pageSize,
                skip: page * pageSize,
                boxId: searchBoxId
            })
            setDevices(list)
            setTotal(total)
        } catch (err: any) {
            console.error("Load devices failed", err)
            Swal.fire("โหลดข้อมูลไม่สำเร็จ", err.message, "error")
        }
    }

    const handleSearch = () => {
        setPage(0)
        load()
    }

    useEffect(() => { load() }, [])

    const handleDelete = async (id: number) => {
        if (isInspector) return // ❌ Inspector ห้ามลบ

        const result = await Swal.fire({
            title: "คุณแน่ใจหรือไม่?",
            text: "คุณกำลังจะลบ อุปกรณ์ GPS นี้",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "ลบ",
            cancelButtonText: "ยกเลิก",
        });

        if (result.isConfirmed) {
            try {
                await deleteDevice(id);
                await Swal.fire("ลบเรียบร้อย ✅", "", "success");
                load();
            } catch (err: any) {
                Swal.fire("ลบไม่สำเร็จ ❌", err.message, "error");
            }
        }
    };

    const columns: GridColDef[] = [
        { field: 'boxId', headerName: 'Box ID', flex: 1, minWidth: 120 },
        { field: 'simNumber', headerName: 'SIM Number', flex: 1, minWidth: 150 },
        { field: 'firmware', headerName: 'Firmware', flex: 1, minWidth: 150 },
        {
            field: 'actions',
            headerName: 'การจัดการ',
            sortable: false,
            filterable: false,
            width: 180,
            renderCell: (params) => (
                !isInspector && ( // 👈 Inspector จะไม่เห็น action buttons เลย
                    <Box display="flex" gap={1}>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => navigate(`/devices/edit/${params.row.id}`)}
                            startIcon={<Edit />}
                            color="warning"
                        >
                            แก้ไข
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<Delete />}
                            onClick={() => handleDelete(params.row.id)}
                        >
                            ลบ
                        </Button>
                    </Box>
                )
            ),
        },
    ]

    return (
        <Box>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h4">รายการอุปกรณ์ GPS</Typography>
                <Button
                    variant="contained"
                    onClick={() => navigate('/devices/create')}
                    sx={{ backgroundColor: '#FEDC00', color: '#000' }}
                    startIcon={<Add />}
                    disabled={isInspector}
                >
                    เพิ่มข้อมูล
                </Button>
            </Box>
            <Paper
                sx={{
                    p: 2,
                    mb: 2,
                    border: '1px solid #ddd',
                    backgroundColor: '#fafafa',
                }}
            >
                <Box display="flex" alignItems="center" gap={2}>
                    <TextField
                        label="ค้นหา BoxId"
                        variant="outlined"
                        size="small"
                        sx={{ width: 260 }} // ✅ จำกัดความกว้าง
                        value={searchBoxId}
                        onChange={(e) => setSearchBoxId(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        startIcon={<Search />}
                        sx={{ height: 40 }}
                    >
                        ค้นหา
                    </Button>
                </Box>
            </Paper>


            <Paper sx={{ height: 500, width: '100%', p: 1 }}>
                <DataGrid
                    rows={device}
                    columns={columns}
                    rowCount={total}
                    paginationMode="server"
                    pageSizeOptions={[10, 15, 25]}
                    paginationModel={{ page, pageSize }}
                    onPaginationModelChange={(model) => {
                        setPage(model.page)
                        setPageSize(model.pageSize)
                    }}
                    disableRowSelectionOnClick
                    getRowId={(row) => row.id}
                    sx={{
                        border: 0,
                        '& .MuiDataGrid-columnHeader': {
                            backgroundColor: '#FEDC00',
                            color: '#000',
                            fontWeight: 'bold',
                        },
                        '& .MuiDataGrid-row:nth-of-type(odd)': {
                            backgroundColor: '#FFFDE7',
                        },
                        '& .MuiDataGrid-row:hover': {
                            backgroundColor: '#FFF59D',
                        },
                    }}
                />
            </Paper>
        </Box>
    )
}
